import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import PremiumLock from "../../components/PremiumLock";
import { 
  ArrowLeft,
  ClipboardCheck,
  Scale,
  Flame,
  Zap,
  Target,
  Moon,
  Brain,
  Footprints,
  Dumbbell,
  MessageSquare,
  CheckCircle2,
  Calendar,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slider component for 1-10 ratings
const RatingSlider = ({ label, icon: Icon, value, onChange, color, description }) => {
  const colorClasses = {
    orange: { bg: "bg-orange-500", border: "border-orange-500", text: "text-orange-400" },
    yellow: { bg: "bg-yellow-500", border: "border-yellow-500", text: "text-yellow-400" },
    green: { bg: "bg-green-500", border: "border-green-500", text: "text-green-400" },
    blue: { bg: "bg-blue-500", border: "border-blue-500", text: "text-blue-400" },
    purple: { bg: "bg-purple-500", border: "border-purple-500", text: "text-purple-400" },
    red: { bg: "bg-red-500", border: "border-red-500", text: "text-red-400" },
  };
  
  const colors = colorClasses[color] || colorClasses.orange;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn("w-5 h-5", colors.text)} />
          <span className="text-white font-medium text-sm">{label}</span>
        </div>
        <span className={cn("text-xl font-heading font-black", colors.text)}>{value}</span>
      </div>
      
      {description && (
        <p className="text-[#666] text-xs">{description}</p>
      )}
      
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={cn(
              "flex-1 h-8 rounded-md transition-all border",
              num <= value 
                ? `${colors.bg} ${colors.border}` 
                : "bg-[#1A1A1A] border-[#2A2A2A] hover:bg-[#2A2A2A]"
            )}
          />
        ))}
      </div>
    </div>
  );
};

const CheckinPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, isPremium } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [expandedCheckin, setExpandedCheckin] = useState(null);
  
  // Form states
  const [weight, setWeight] = useState("");
  const [hambre, setHambre] = useState(5);
  const [energia, setEnergia] = useState(5);
  const [adherencia, setAdherencia] = useState(5);
  const [sueno, setSueno] = useState(5);
  const [estres, setEstres] = useState(5);
  const [entrenamientos, setEntrenamientos] = useState("");
  const [pasos, setPasos] = useState("");
  const [comentarios, setComentarios] = useState("");

  useEffect(() => {
    if (isPremium) {
      fetchCheckins();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const fetchCheckins = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/premium/checkins-v2`, {
        headers: getAuthHeaders(),
      });
      const data = response.data.checkins || [];
      setCheckins(data);
      
      // Check if user already did a check-in this week
      if (data.length > 0) {
        const lastCheckin = data[0];
        const lastCheckinDate = new Date(lastCheckin.fecha);
        const now = new Date();
        const daysSince = Math.floor((now - lastCheckinDate) / (1000 * 60 * 60 * 24));
        
        if (daysSince < 5) {
          setShowForm(false);
        }
      }
    } catch (error) {
      console.error("Error fetching checkins:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const weightNum = parseFloat(weight);
    if (isNaN(weightNum) || weightNum < 30 || weightNum > 300) {
      toast.error("Introduce un peso válido (30-300 kg)");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/premium/checkin-v2`,
        {
          peso_actual: weightNum,
          hambre: hambre,
          energia: energia,
          adherencia: adherencia,
          sueno: sueno,
          estres: estres,
          entrenamientos_completados: parseInt(entrenamientos) || 0,
          pasos_medios: parseInt(pasos) || null,
          comentarios: comentarios || null
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Check-in guardado correctamente");
      // Reset form
      setWeight("");
      setHambre(5);
      setEnergia(5);
      setAdherencia(5);
      setSueno(5);
      setEstres(5);
      setEntrenamientos("");
      setPasos("");
      setComentarios("");
      setShowForm(false);
      fetchCheckins();
    } catch (error) {
      console.error("Error saving checkin:", error);
      toast.error("Error al guardar el check-in");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short"
    });
  };

  // FREE user - Show PremiumLock
  if (!isPremium) {
    return (
      <div className="animate-fade-in pb-8" data-testid="checkin-page-locked">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate("/plan")}
            className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#888]" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-black text-white">Check-in Semanal</h1>
            <p className="text-[#666] text-sm">Mentoría con tu coach</p>
          </div>
        </div>
        <PremiumLock
          title="Disponible en Premium"
          description="Haz seguimiento semanal con tu coach para optimizar tu progreso"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#666] mt-4">Cargando check-ins...</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in pb-24" data-testid="checkin-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/progreso")}
          className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-[#888]" />
        </button>
        <div>
          <h1 className="text-2xl font-heading font-black text-white">Check-in Semanal</h1>
          <p className="text-[#666] text-sm">Seguimiento con tu coach</p>
        </div>
      </div>

      {/* Form or Toggle */}
      {!showForm && checkins.length > 0 && (
        <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              <div className="flex-1">
                <p className="text-white font-semibold">Check-in completado</p>
                <p className="text-green-400 text-sm">Último: {formatDate(checkins[0]?.fecha)}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowForm(true)}
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              >
                Nuevo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          {/* Weight */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Scale className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white font-semibold">Peso actual</span>
              </div>
              <Input
                type="number"
                step="0.1"
                placeholder="Ej: 75.5 kg"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-lg font-semibold"
                required
              />
            </CardContent>
          </Card>

          {/* Ratings */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4 space-y-5">
              <RatingSlider
                label="Hambre"
                icon={Flame}
                value={hambre}
                onChange={setHambre}
                color="orange"
                description="1 = Sin hambre, 10 = Hambre extrema"
              />
              <RatingSlider
                label="Energía"
                icon={Zap}
                value={energia}
                onChange={setEnergia}
                color="yellow"
                description="1 = Agotado, 10 = Lleno de energía"
              />
              <RatingSlider
                label="Adherencia"
                icon={Target}
                value={adherencia}
                onChange={setAdherencia}
                color="green"
                description="1 = No seguí el plan, 10 = Perfecto cumplimiento"
              />
              <RatingSlider
                label="Sueño"
                icon={Moon}
                value={sueno}
                onChange={setSueno}
                color="blue"
                description="1 = Muy mal, 10 = Excelente descanso"
              />
              <RatingSlider
                label="Estrés"
                icon={Brain}
                value={estres}
                onChange={setEstres}
                color="red"
                description="1 = Muy relajado, 10 = Muy estresado"
              />
            </CardContent>
          </Card>

          {/* Activity */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4 space-y-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Dumbbell className="w-5 h-5 text-[#D4AF37]" />
                Actividad
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#888] text-xs mb-1 block">Entrenamientos completados</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={entrenamientos}
                    onChange={(e) => setEntrenamientos(e.target.value)}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs mb-1 block">Pasos medios/día</label>
                  <Input
                    type="number"
                    placeholder="8000"
                    value={pasos}
                    onChange={(e) => setPasos(e.target.value)}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white font-semibold">Comentarios</span>
              </div>
              <Textarea
                placeholder="¿Cómo te has sentido esta semana? ¿Alguna dificultad?"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[100px]"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full py-6 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold text-lg"
          >
            {saving ? "Guardando..." : "Enviar Check-in"}
          </Button>
        </form>
      )}

      {/* History */}
      {checkins.length > 0 && (
        <div>
          <h2 className="text-white font-heading font-bold text-lg mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            Historial de Check-ins
          </h2>
          <div className="space-y-2">
            {checkins.map((checkin) => (
              <Card 
                key={checkin.id} 
                className="bg-[#121212] border-[#2A2A2A] cursor-pointer hover:border-[#3A3A3A] transition-colors"
                onClick={() => setExpandedCheckin(expandedCheckin === checkin.id ? null : checkin.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                        <ClipboardCheck className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">{formatDate(checkin.fecha)}</p>
                        <p className="text-[#888] text-sm">{checkin.peso_actual} kg</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[#1A1A1A] text-[#888]">
                        E: {checkin.energia}/10
                      </Badge>
                      {expandedCheckin === checkin.id ? (
                        <ChevronUp className="w-5 h-5 text-[#666]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#666]" />
                      )}
                    </div>
                  </div>
                  
                  {expandedCheckin === checkin.id && (
                    <div className="mt-4 pt-4 border-t border-[#2A2A2A] grid grid-cols-3 gap-2">
                      <div className="text-center p-2 bg-[#1A1A1A] rounded">
                        <p className="text-[#666] text-xs">Hambre</p>
                        <p className="text-white font-bold">{checkin.hambre}/10</p>
                      </div>
                      <div className="text-center p-2 bg-[#1A1A1A] rounded">
                        <p className="text-[#666] text-xs">Adherencia</p>
                        <p className="text-white font-bold">{checkin.adherencia}/10</p>
                      </div>
                      <div className="text-center p-2 bg-[#1A1A1A] rounded">
                        <p className="text-[#666] text-xs">Sueño</p>
                        <p className="text-white font-bold">{checkin.sueno}/10</p>
                      </div>
                      <div className="text-center p-2 bg-[#1A1A1A] rounded">
                        <p className="text-[#666] text-xs">Estrés</p>
                        <p className="text-white font-bold">{checkin.estres}/10</p>
                      </div>
                      <div className="text-center p-2 bg-[#1A1A1A] rounded">
                        <p className="text-[#666] text-xs">Entrenos</p>
                        <p className="text-white font-bold">{checkin.entrenamientos_completados}</p>
                      </div>
                      <div className="text-center p-2 bg-[#1A1A1A] rounded">
                        <p className="text-[#666] text-xs">Pasos</p>
                        <p className="text-white font-bold">{checkin.pasos_medios || "-"}</p>
                      </div>
                      {checkin.comentarios && (
                        <div className="col-span-3 p-2 bg-[#1A1A1A] rounded mt-2">
                          <p className="text-[#888] text-sm">{checkin.comentarios}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckinPage;
